/* eslint react-hooks/exhaustive-deps: "warn" */

import { useMemo, useState } from 'react';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';

import styles from './HubListPage.module.scss';

import { HubGroupRef } from './HubRoute.types';
import { IndexSection } from './atoms/IndexSection/IndexSection';
import { HubTopicCard } from './content.types';
import { groupPath, hubPath } from './paths';
import { BreadcrumbItem } from './molecules/Breadcrumb/Breadcrumb.types';
import { HubFilters } from './molecules/HubFilters/HubFilters';
import { HubHero } from './molecules/HubHero/HubHero';
import { HubFilterOption } from './molecules/HubFilters/HubFilters.types';
import { TopicCard } from './molecules/TopicCard/TopicCard';
import { IndexAreas } from './sections/IndexAreas/IndexAreas';
import { IndexEditorial } from './sections/IndexEditorial/IndexEditorial';
import { IndexFeatured } from './sections/IndexFeatured/IndexFeatured';
import { IndexGuides } from './sections/IndexGuides/IndexGuides';
import { IndexHero } from './sections/IndexHero/IndexHero';
import type { HubListPageProps } from './HubListPage.types';

const PAGE_STEP = 18;
const ALL = 'all';
const TOPICS_ID = 'topics';

const STOP_WORDS = new Set(['what', 'is', 'are', 'a', 'an', 'the', 'how', 'do', 'does', 'to', 'your']);

const matches = (card: HubTopicCard, query: string): boolean => {
  const haystack = `${card.name} ${card.definition} ${card.subgroup_name}`.toLowerCase();
  return query
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((token) => token && !STOP_WORDS.has(token))
    .every((token) => haystack.includes(token));
};

const normalise = (
  data: HubListPageProps['data'],
): {
  title: string;
  description: string;
  cards: HubTopicCard[];
  groups: HubGroupRef[];
  subgroups: HubFilterOption[];
  breadcrumb: BreadcrumbItem[];
} => {
  if (data.kind === 'hub') {
    const subgroups = Array.from(new Map(data.cards.map((c) => [c.subgroup, c.subgroup_name])).entries()).map(
      ([slug, label]) => ({ slug, label }),
    );
    return {
      title: 'Knowledge Hub',
      description: 'Plain-English answers to 300 company-data, compliance, ownership and registry questions.',
      cards: data.cards,
      groups: data.groups,
      subgroups,
      breadcrumb: [{ label: 'Knowledge' }],
    };
  }

  if (data.kind === 'group') {
    const cards = data.families.flatMap((f) => f.cards);
    return {
      title: data.group.title,
      description: data.group.description,
      cards,
      groups: [],
      subgroups: data.families.map((f) => ({ slug: f.slug, label: f.name })),
      breadcrumb: [{ label: 'Knowledge' }, { label: data.group.title }],
    };
  }

  return {
    title: data.family.name,
    description: `${data.family.name} - ${data.group.title}.`,
    cards: data.cards,
    groups: [],
    subgroups: [],
    breadcrumb: [{ label: 'Knowledge' }, { label: data.group.title }, { label: data.family.name }],
  };
};

export const HubListPage = ({ data, lang }: HubListPageProps) => {
  const { t } = useInt();
  const { title, description, cards, groups, subgroups, breadcrumb } = useMemo(() => normalise(data), [data]);

  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(ALL);
  const [subgroup, setSubgroup] = useState(ALL);
  const [limit, setLimit] = useState(PAGE_STEP);

  // Link group/family labels in the breadcrumb (last stays plain text).
  const linkedCrumb: BreadcrumbItem[] = breadcrumb.map((item, index) => {
    if (index === 0) {
      return { ...item, href: hubPath(lang) };
    }

    if (data.kind === 'family' && index === 1) {
      return { ...item, href: groupPath(data.group.slug, lang) };
    }

    return item;
  });

  const filtered = useMemo(
    () =>
      cards.filter(
        (card) =>
          (group === ALL || card.group === group) &&
          (subgroup === ALL || card.subgroup === subgroup) &&
          matches(card, query),
      ),
    [cards, group, subgroup, query],
  );

  const familyOptions = useMemo(
    () =>
      group === ALL
        ? subgroups
        : subgroups.filter((option) => cards.some((card) => card.group === group && card.subgroup === option.slug)),
    [cards, group, subgroups],
  );

  const visible = filtered.slice(0, limit);
  const resultLabel = t('{shown} of {total} topics shown')
    .replace('{shown}', String(visible.length))
    .replace('{total}', String(filtered.length));

  const onQuery = (value: string) => {
    setQuery(value);
    setLimit(PAGE_STEP);
  };
  const onGroup = (slug: string) => {
    setGroup(slug);
    setSubgroup(ALL);
    setLimit(PAGE_STEP);
  };
  const onSubgroup = (slug: string) => {
    setSubgroup(slug);
    setLimit(PAGE_STEP);
  };

  const onHeroSearch = (value: string) => {
    onQuery(value);
    setGroup(ALL);
    setSubgroup(ALL);
    document.getElementById(TOPICS_ID)?.scrollIntoView({ behavior: 'smooth' });
  };

  const directory = (
    <>
      <HubFilters
        query={query}
        onQuery={onQuery}
        groups={groups}
        activeGroup={group}
        onGroup={onGroup}
        subgroups={familyOptions}
        activeSubgroup={subgroup}
        onSubgroup={onSubgroup}
        resultLabel={resultLabel}
        className={styles['filters']}
      />

      {visible.length > 0 ? (
        <ul className={styles['grid']}>
          {visible.map((card) => (
            <li key={card.slug} className={styles['cell']}>
              <TopicCard topic={card} lang={lang} />
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles['empty']}>{t('No topics match your search.')}</p>
      )}

      {visible.length < filtered.length && (
        <div className={styles['more']}>
          <Button type="dark-outline" size="lg" onClick={() => setLimit((l) => l + PAGE_STEP)}>
            {t('Show more topics')}
          </Button>
        </div>
      )}
    </>
  );

  if (data.kind === 'hub') {
    return (
      <div>
        <IndexHero
          topics={cards.length}
          areas={groups.length}
          families={subgroups.length}
          onSearch={onHeroSearch}
          lang={lang}
        />

        <IndexAreas groups={groups} families={subgroups.length} lang={lang} />

        <IndexEditorial lang={lang} />

        <IndexFeatured lang={lang} />

        <IndexSection
          id={TOPICS_ID}
          title={t('All topics, connected.')}
          aside={t('Search all {total} topics or choose an area and a topic family.').replace(
            '{total}',
            String(cards.length),
          )}
        >
          {directory}
        </IndexSection>

        <IndexGuides guides={data.guides} />

        <CtaSection
          theme="dark"
          spotlight
          title={t('Go from understanding to evidence.')}
          description={t('Connect the concepts to company information you can use in your workflow.')}
          actions={
            <Button type="white" size="xl" href="/kyb-verification" suffixIcon="arrow-forward">
              {t('Explore business verification')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles['page']}>
      <HubHero breadcrumb={linkedCrumb} title={title} description={description} />

      <div className={styles['inner']}>{directory}</div>
    </div>
  );
};
